import React, { useEffect, useState, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { useStyles, useTranslations } from "examsbook-react-sdk/custom-hooks";
import { Box, Modal, IconButton, Button, Dialog, DialogContent, DialogActions, Snackbar } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import ZoomOutIcon from "@material-ui/icons/ZoomOut";
import { ColorsConstant } from "../../constants/colorConstant";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import VisibilityIcon from "@material-ui/icons/Visibility";
import PublishIcon from "@material-ui/icons/Publish";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import CancelIcon from "@material-ui/icons/Cancel";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { useDispatch } from "react-redux";
import { submitAssignmentAction, getAssignmentAction, resubmitAssignmentAction } from "examsbook-react-sdk/actions/assignmentAction";

function buildFileUrl(filePath) {
    if (!filePath) {
        return "";
    }
    if (filePath.startsWith("http")) {
        return filePath;
    }
    const prefix = filePath.startsWith("/") ? "" : "/";
    return `${process.env.REACT_APP_BACKEND_URL}${prefix}${filePath}`;
}

function createSubmissionEntry(submissionNumber, date, fileData) {
    const filePath = fileData?.filePath || fileData?.url || fileData?.fileUrl || fileData?.path || "";
    return {
        type: "submission",
        submissionNumber,
        date,
        fileName: fileData?.fileName || fileData?.name || fileData?.originalName || "Assignment File",
        fileUrl: buildFileUrl(filePath),
    };
}

function createReviewEntry(reviewData) {
    return {
        type: "review",
        date: reviewData?.createdAt || reviewData?.date,
        remark: reviewData?.message,
        status: reviewData?.status,
        reviewerName: reviewData?.reviewerName,
    };
}

function extractResponses(assignmentData) {
    const responses = Array.isArray(assignmentData?.responses) ? assignmentData.responses : [];
    return {
        userSubmissions: responses.filter((response) => Array.isArray(response.attachments) && response.attachments.length > 0),
        adminReviews: responses.filter((response) => !Array.isArray(response.attachments) || response.attachments.length === 0),
    };
}

function getLatestAttachment(assignmentData) {
    const responses = Array.isArray(assignmentData?.responses) ? assignmentData.responses : [];
    for (let i = responses.length - 1; i >= 0; i--) {
        const responseAttachment = responses[i]?.attachments?.[0];
        if (responseAttachment) {
            return responseAttachment;
        }
    }
    return assignmentData?.attachments?.[0] || null;
}

function buildAssignmentHistoryData(assignmentData, initialSubmission) {
    if (!assignmentData) {
        return [];
    }

    const history = [];
    const { userSubmissions, adminReviews } = extractResponses(assignmentData);
    let submissionCount = 0;

    const initialSource = userSubmissions.length > 0
        ? (initialSubmission || assignmentData?.attachments?.[0])
        : assignmentData?.attachments?.[0];

    if (initialSource) {
        submissionCount += 1;
        history.push(createSubmissionEntry(
            submissionCount,
            initialSource?.date || assignmentData?.createdAt || assignmentData?.date,
            initialSource
        ));
    }

    if (adminReviews[0]) {
        history.push(createReviewEntry(adminReviews[0]));
    }

    if (userSubmissions.length > 0) {
        userSubmissions.forEach((submission, index) => {
            const attachment = submission?.attachments?.[0];
            if (!attachment) {
                return;
            }
            submissionCount += 1;
            history.push(createSubmissionEntry(submissionCount, submission?.createdAt || submission?.date, attachment));
            const mappedReview = adminReviews[index + 1];
            if (mappedReview) {
                history.push(createReviewEntry(mappedReview));
            }
        });
    }

    return history;
}

function getOverallStatus(status) {
    if (status === "APPROVED") {
        return "approved";
    }
    if (status === "REJECTED") {
        return "rejected";
    }
    return "pending";
}

function getStatusChipMeta(overallStatus, t) {
    if (overallStatus === "approved") {
        return { color: ColorsConstant.Success, label: `✓ ${t("Assignment Approved")}` };
    }
    if (overallStatus === "rejected") {
        return { color: "#dc3545", label: `✗ ${t("Assignment Rejected")}` };
    }
    return { color: ColorsConstant.yellow, label: `⏳ ${t("Pending Review")}` };
}

function LearningText(props) {
    const {
        courseId,
        sectionId,
        courseSectionDataId,
        onVideoRefresh,
        videoRefresh,
        textData,
        textName,
        showAssignmentUpload
    } = props;
    const classes = useStyles(styles)();
    const [open, setOpen] = useState(false);
    const [imgSrc, setImgSrc] = useState("");
    const [zoom, setZoom] = useState(1);
    const contentRef = useRef(null);
    const t = useTranslations();
    const dispatch = useDispatch();
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [assignmentData, setAssignmentData] = useState(null);
    const [remarksDialog, setRemarksDialog] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState(false);
    const [resubmitDialog, setResubmitDialog] = useState(false);
    const [resubmitFile, setResubmitFile] = useState(null);
    const fileInputRef = useRef(null);
    const resubmitFileInputRef = useRef(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [initialSubmission, setInitialSubmission] = useState(null);

    const assignmentHistory = useMemo(
        () => buildAssignmentHistoryData(assignmentData, initialSubmission),
        [assignmentData, initialSubmission]
    );
    const overallStatus = getOverallStatus(assignmentData?.status);
    const statusChipMeta = getStatusChipMeta(overallStatus, t);
    const snackbarColors = {
        success: '#4caf50',
        error: '#f44336',
        warning: '#ff9800',
        info: '#2196f3',
    };
    const snackbarBackgroundColor = snackbarColors[snackbar.severity] || snackbarColors.info;

    const syncAssignmentData = (data) => {
        setAssignmentData(data);
        const latestFile = getLatestAttachment(data);
        if (latestFile) {
            const fileName = latestFile?.fileName || latestFile?.name;
            setUploadedFile({ name: fileName });
            return;
        }
        setUploadedFile(null);
    };

    const refreshAssignmentData = async () => {
        const updatedData = await dispatch(getAssignmentAction(courseId, sectionId, courseSectionDataId));
        if (updatedData?.status && updatedData?.data) {
            syncAssignmentData(updatedData.data);
        }
    };

    const cacheInitialSubmission = (data) => {
        const storageKey = `initial_submission_${data._id}`;
        const storedInitial = localStorage.getItem(storageKey);
        if (storedInitial) {
            setInitialSubmission(JSON.parse(storedInitial));
            return;
        }
        const firstAttachment = data?.attachments?.[0];
        if (!firstAttachment) {
            return;
        }
        const initialData = {
            fileName: firstAttachment.fileName,
            filePath: firstAttachment.filePath,
            date: data.createdAt,
        };
        localStorage.setItem(storageKey, JSON.stringify(initialData));
        setInitialSubmission(initialData);
    };

    const handleHistoryFilePreview = (fileUrl, fileName) => {
        // If fileUrl is provided from server, use it; otherwise show message
        if (fileUrl) {
            window.open(fileUrl, '_blank');
        } else {
            setSnackbar({ open: true, message: t('File preview is not available.'), severity: "info" });
        }
    };

    // Fetch assignment data on component mount
    useEffect(() => {
        if (!(courseId && sectionId && courseSectionDataId)) {
            return;
        }

        const fetchAssignment = async () => {
            try {
                const response = await dispatch(getAssignmentAction(courseId, sectionId, courseSectionDataId));
                if (!(response?.status && response?.data)) {
                    return;
                }

                cacheInitialSubmission(response.data);
                syncAssignmentData(response.data);

                if (response.data.status === "APPROVED" && onVideoRefresh) {
                    onVideoRefresh(!videoRefresh);
                }
            } catch (error) {
                console.error("Error fetching assignment:", error);
            }
        };

        fetchAssignment();
    }, [courseId, sectionId, courseSectionDataId, dispatch, onVideoRefresh, videoRefresh]);

    useEffect(() => {
        if (contentRef.current) {
            const images = contentRef.current.querySelectorAll("img");
            images.forEach((img) => {
                img.style.cursor = "pointer";
                img.onclick = () => {
                    setImgSrc(img.src);
                    setZoom(1); // reset zoom each time
                    setOpen(true);
                };
            });
        }
    }, [textData?.detail]);

    const handleZoomIn = () => setZoom((z) => Math.min(z + 0.2, 3));
    const handleZoomOut = () => setZoom((z) => Math.max(z - 0.2, 0.5));

    const handleFileChange = (e) => {
        const nextFile = e.target.files?.[0];
        if (nextFile) {
            setSelectedFile(nextFile);
        }
    };

    const handleCancelFile = () => {
        setSelectedFile(null);
    };

    const handleUploadClick = () => {
        if (!selectedFile) return;
        setConfirmDialog(true);
    };

    const handleConfirmSave = async () => {
        setConfirmDialog(false);
        if (!selectedFile) return;

        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("courseId", courseId);
        formData.append("moduleId", sectionId);
        formData.append("sectionId", courseSectionDataId);
        formData.append("message", "Assignment submission");

        try {
            const response = await dispatch(submitAssignmentAction(formData));

            if (response.status) {
                await refreshAssignmentData();
                setSelectedFile(null);
                setSnackbar({ open: true, message: response.msg || t('Assignment submitted successfully!'), severity: "success" });
            }
        } catch (error) {
            console.error("Error:", error);
            setSnackbar({ open: true, message: error.response?.data?.message || t('Failed to upload assignment. Please try again.'), severity: "error" });
        }
    };

    const handlePreview = () => {
        if (selectedFile) {
            const fileURL = URL.createObjectURL(selectedFile);
            if (selectedFile.type === 'application/msword' ||
                selectedFile.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
                setSnackbar({ open: true, message: t('Preview is not available for Word documents. The file will be downloaded.'), severity: "info" });
                const link = document.createElement('a');
                link.href = fileURL;
                link.download = selectedFile.name;
                link.click();
            } else {
                window.open(fileURL, '_blank');
            }
        }
    };

    const handleResubmitFileChange = (e) => {
        const nextFile = e.target.files?.[0];
        if (nextFile) {
            setResubmitFile(nextFile);
        }
    };

    const handleResubmitClick = () => {
        if (!resubmitFile) return;
        setResubmitDialog(true);
    };

    const handleConfirmResubmit = async () => {
        setResubmitDialog(false);
        if (!resubmitFile || !assignmentData?._id) return;

        const formData = new FormData();
        formData.append("file", resubmitFile);
        formData.append("message", "Resubmitting assignment");

        try {
            const response = await dispatch(resubmitAssignmentAction(assignmentData._id, formData));

            if (response.status) {
                await refreshAssignmentData();
                setResubmitFile(null);
                setSnackbar({ open: true, message: response.msg || t('Assignment resubmitted successfully!'), severity: "success" });
            }
        } catch (error) {
            console.error("Error:", error);
            setSnackbar({ open: true, message: error.response?.data?.msg || t('Failed to resubmit assignment. Please try again.'), severity: "error" });
        }
    };

    return (
        <Box>
            <Box className={classes.titleContainer}>
                <div className={classes.textTitle}>{textName}</div>
                {uploadedFile && showAssignmentUpload && (
                    <Button
                        variant="contained"
                        size="small"
                        onClick={() => setRemarksDialog(true)}
                        style={{ backgroundColor: ColorsConstant.defaultBlue, color: ColorsConstant.white, textTransform: "none" }}
                    >
                        {t('Reports')}
                    </Button>
                )}
            </Box>
            <div
                className={`${classes.textData} ql-editor`}
                ref={contentRef}
                dangerouslySetInnerHTML={{ __html: textData?.detail }}
            />

            {showAssignmentUpload && (
                <>
                    {!uploadedFile && !selectedFile && (
                        <Box className={classes.uploadSection} onClick={() => fileInputRef.current?.click()} style={{ cursor: "pointer" }}>
                            <CloudUploadIcon style={{ fontSize: "48px", color: "#ccc" }} />
                            <p style={{ fontSize: "16px", fontWeight: 500, color: "#333", margin: "16px 0 8px 0" }}>
                                {t('No assignment uploaded yet')}
                            </p>
                            <p style={{ fontSize: "14px", color: "#666", margin: 0 }}>
                                {t('Upload your assignment to get it reviewed by the examiner.')}
                            </p>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                onChange={handleFileChange}
                                style={{ display: "none" }}
                            />
                        </Box>
                    )}

                    {selectedFile && (
                        <Box className={classes.selectedFileSection}>
                            <Box className={classes.selectedFileContainer}>
                                <Box className={classes.selectedFileInfo}>
                                    <InsertDriveFileIcon className={classes.selectedFileIcon} />
                                    <Box className={classes.selectedFileTextBox}>
                                        <p className={classes.selectedFileName}>{selectedFile.name}</p>
                                        <p className={classes.selectedFileDate}>
                                            {t('Uploaded on')} {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                                        </p>
                                    </Box>
                                    <IconButton size="small" onClick={handleCancelFile} className={classes.cancelIconButton}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                                <Box className={classes.selectedFileActions}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<VisibilityIcon />}
                                        onClick={handlePreview}
                                        className={classes.previewButton}
                                    >
                                        {t('Preview')}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<PublishIcon />}
                                        onClick={handleUploadClick}
                                        className={classes.uploadButton}
                                    >
                                        {t('Submit')}
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {uploadedFile && !selectedFile && (
                        <>
                            {resubmitFile ? (
                                <Box className={classes.selectedFileSection}>
                                    <Box className={classes.selectedFileContainer}>
                                        <Box className={classes.selectedFileInfo}>
                                            <InsertDriveFileIcon className={classes.selectedFileIcon} />
                                            <Box className={classes.selectedFileTextBox}>
                                                <p className={classes.selectedFileName}>{resubmitFile.name}</p>
                                                <p className={classes.selectedFileDate}>
                                                    {t('Selected on')} {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                                                </p>
                                            </Box>
                                            <IconButton size="small" onClick={() => setResubmitFile(null)} className={classes.cancelIconButton}>
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                        <Box className={classes.selectedFileActions}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<VisibilityIcon />}
                                                onClick={() => {
                                                    const fileURL = URL.createObjectURL(resubmitFile);
                                                    if (resubmitFile.type === 'application/msword' || resubmitFile.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
                                                        setSnackbar({ open: true, message: t('Preview is not available for Word documents. The file will be downloaded.'), severity: "info" });
                                                        const link = document.createElement('a');
                                                        link.href = fileURL;
                                                        link.download = resubmitFile.name;
                                                        link.click();
                                                    } else {
                                                        window.open(fileURL, '_blank');
                                                    }
                                                }}
                                                className={classes.previewButton}
                                            >
                                                {t('Preview')}
                                            </Button>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={<PublishIcon />}
                                                onClick={handleResubmitClick}
                                                className={classes.uploadButton}
                                            >
                                                {t('Submit')}
                                            </Button>
                                        </Box>
                                    </Box>
                                </Box>
                            ) : (
                                <Box className={classes.uploadedSection}>
                                    <Box className={classes.uploadedFileContainer}>
                                        <Box className={classes.fileIconBox}>
                                            <InsertDriveFileIcon className={classes.fileIcon} />
                                        </Box>
                                        <Box className={classes.fileInfoBox}>
                                            <p className={classes.fileNameText}>{uploadedFile.name}</p>
                                            <p className={classes.fileDateText}>
                                                {t('Uploaded on')} {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                                            </p>
                                        </Box>
                                    </Box>
                                </Box>
                            )}


                            {assignmentData?.status === "PENDING" && (
                                <Box className={classes.statusBanner} style={{ backgroundColor: "#fff3cd", border: "1px solid #ffc107" }}>
                                    <AccessTimeIcon style={{ color: "#ff9800", fontSize: "18px" }} />
                                    <span style={{ color: "#856404", fontSize: "14px" }}>
                                        {t('Your assignment is currently under review. Please wait for the examiner to evaluate your submission.')}
                                    </span>
                                </Box>
                            )}

                            {assignmentData?.status === "REJECTED" && !resubmitFile && (
                                <>
                                    <Box className={classes.statusBanner} style={{ backgroundColor: "#f8d7da", border: "1px solid #f5c6cb" }}>
                                        <CancelIcon style={{ color: "#dc3545", fontSize: "18px" }} />
                                        <span style={{ color: "#721c24", fontSize: "14px" }}>
                                            {t('Your assignment is rejected by the examiner. Kindly go through the learning materials again and then re-upload the assignment.')}
                                        </span>
                                    </Box>
                                    {!resubmitFile && (
                                        <Button
                                            variant="contained"
                                            startIcon={<PublishIcon />}
                                            onClick={() => resubmitFileInputRef.current?.click()}
                                            style={{
                                                marginTop: "16px",
                                                backgroundColor: ColorsConstant.defaultBlue,
                                                color: ColorsConstant.white,
                                                textTransform: "none"
                                            }}
                                        >
                                            {t('Resubmit Assignment')}
                                        </Button>
                                    )}
                                    <input
                                        ref={resubmitFileInputRef}
                                        type="file"
                                        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                        onChange={handleResubmitFileChange}
                                        style={{ display: "none" }}
                                    />
                                </>
                            )}

                            {assignmentData?.status === "APPROVED" && (
                                <Box className={classes.statusBanner} style={{ backgroundColor: "#d4edda", border: "1px solid #c3e6cb" }}>
                                    <CheckCircleIcon style={{ color: "#28a745", fontSize: "18px" }} />
                                    <span style={{ color: "#155724", fontSize: "14px" }}>
                                        {t('Your assignment has been successfully approved.')}
                                    </span>
                                </Box>
                            )}
                        </>
                    )}
                </>
            )}



            <Dialog open={resubmitDialog} onClose={() => setResubmitDialog(false)}>
                <DialogContent>
                    <p>{t('Are you sure you want to resubmit this assignment?')}</p>
                    <DialogActions>
                        <Button onClick={() => setResubmitDialog(false)}>{t('CANCEL')}</Button>
                        <Button
                            onClick={handleConfirmResubmit}
                            variant="contained"
                            style={{ backgroundColor: ColorsConstant.defaultBlue, color: ColorsConstant.white }}
                        >
                            {t('Resubmit')}
                        </Button>
                    </DialogActions>
                </DialogContent>
            </Dialog>

            <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
                <DialogContent>
                    <p>{t('Are you sure you want to submit this assignment? It won\'t be changed after submission until reviewed.')}</p>
                    <DialogActions>
                        <Button onClick={() => setConfirmDialog(false)}>{t('CANCEL')}</Button>
                        <Button
                            onClick={handleConfirmSave}
                            variant="contained"
                            style={{ backgroundColor: ColorsConstant.defaultBlue, color: ColorsConstant.white }}
                        >
                            {t('Yes')}
                        </Button>
                    </DialogActions>
                </DialogContent>
            </Dialog>

            <Dialog
                open={remarksDialog}
                onClose={() => setRemarksDialog(false)}
                maxWidth="md"
                PaperProps={{ style: { width: '100%' } }}
            >
                <DialogContent style={{ padding: "24px" }}>
                    <h2 style={{ margin: "0 0 24px 0", color: ColorsConstant.defaultBlue, textAlign: "center" }}>{t('Assignment Report')}</h2>

                    <Box style={{ position: "relative", paddingLeft: "40px" }}>
                        {assignmentHistory.map((entry) => {
                            const timelineKey = `${entry.type}-${entry.submissionNumber || "review"}-${entry.date || ""}`;
                            return (
                            <Box key={timelineKey} style={{ position: "relative", marginBottom: "32px" }}>
                                <Box style={{ position: "absolute", left: "-33px", top: "8px", width: "12px", height: "12px", borderRadius: "50%", backgroundColor: ColorsConstant.defaultBlue, border: `3px solid ${ColorsConstant.white}`, boxShadow: `0 0 0 2px ${ColorsConstant.defaultBlue}` }} />
                                {assignmentHistory.indexOf(entry) < assignmentHistory.length - 1 && <Box style={{ position: "absolute", left: "-27px", top: "20px", bottom: "-32px", width: "2px", backgroundColor: "#e0e0e0" }} />}
                                <Box style={{ backgroundColor: "#f5f5f5", padding: "16px", borderRadius: "8px" }}>
                                    {entry.type === 'submission' ? (
                                        <>
                                            <p style={{ margin: "0 0 12px 0", fontSize: "15px", color: ColorsConstant.defaultBlue, fontWeight: 600 }}>
                                                {entry.submissionNumber === 1 ? t('Initial Submission') : `${t('Resubmission')} ${entry.submissionNumber - 1}`}
                                            </p>
                                            <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                <p style={{ margin: 0, fontSize: "14px", color: "#666", fontWeight: 500 }}>
                                                    {t('Submitted on')} {new Date(entry.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                                                </p>
                                                <button
                                                    type="button"
                                                    style={{ fontSize: "13px", color: ColorsConstant.defaultBlue, fontWeight: 500, cursor: "pointer", textDecoration: "underline" }}
                                                    onClick={() => handleHistoryFilePreview(entry.fileUrl, entry.fileName)}
                                                >
                                                    {entry.fileName}
                                                </button>
                                            </Box>
                                        </>
                                    ) : (
                                        <>
                                            <p style={{ margin: "0 0 12px 0", fontSize: "15px", color: ColorsConstant.defaultBlue, fontWeight: 600 }}>
                                                {t('Admin Review')}
                                            </p>
                                            <p style={{ margin: "0 0 8px 0", fontSize: "14px", color: "#666", fontWeight: 500 }}>
                                                {t('Reviewed on')} {new Date(entry.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                                                {entry.reviewerName && ` ${t('by')} ${entry.reviewerName}`}
                                            </p>
                                            {entry.status === 'APPROVED' ? (
                                                <Box style={{ backgroundColor: ColorsConstant.white, padding: "12px", borderRadius: "6px", borderLeft: `3px solid #28a745` }}>
                                                    <p style={{ margin: 0, color: "#28a745", fontWeight: 600 }}>{t('Approved by Admin')}</p>
                                                </Box>
                                            ) : (
                                                <Box style={{ backgroundColor: ColorsConstant.white, padding: "12px", borderRadius: "6px", borderLeft: `3px solid ${entry.status === 'REJECTED' ? '#dc3545' : ColorsConstant.defaultBlue}` }}>
                                                    <strong style={{ color: ColorsConstant.defaultBlue }}>{t('Remark')}:</strong>
                                                    <p style={{ margin: "8px 0 0 0" }}>{entry.remark}</p>
                                                </Box>
                                            )}
                                        </>
                                    )}
                                </Box>
                            </Box>
                            );
                        })}

                        <Box style={{ marginTop: "16px", textAlign: "center" }}>
                            <Box style={{ display: "inline-block", padding: "8px 24px", borderRadius: "20px", color: ColorsConstant.white, fontSize: "16px", fontWeight: 500, backgroundColor: statusChipMeta.color }}>
                                {statusChipMeta.label}
                            </Box>
                        </Box>
                    </Box>
                    <DialogActions>
                        <Button onClick={() => setRemarksDialog(false)}>{t('Close')}</Button>
                    </DialogActions>
                </DialogContent>

            </Dialog>

            <Modal open={open} onClose={() => setOpen(false)}>
                <Box className={classes.modalWrapper}>
                    <IconButton
                        className={classes.closeButton}
                        onClick={() => setOpen(false)}
                    >
                        <CloseIcon style={{ color: "white" }} />
                    </IconButton>
                    <IconButton
                        className={classes.zoomInButton}
                        onClick={handleZoomIn}
                    >
                        <ZoomInIcon style={{ color: "white" }} />
                    </IconButton>
                    <IconButton
                        className={classes.zoomOutButton}
                        onClick={handleZoomOut}
                    >
                        <ZoomOutIcon style={{ color: "white" }} />
                    </IconButton>

                    <Box className={classes.modalContent}>
                        <img
                            src={imgSrc}
                            alt="Zoomable"
                            className={classes.modalImage}
                            style={{ transform: `scale(${zoom})` }}
                        />
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                message={snackbar.message}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                ContentProps={{
                    style: {
                        backgroundColor: snackbarBackgroundColor,
                        color: '#fff'
                    }
                }}
            />
        </Box>
    );
}

LearningText.propTypes = {
    courseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    courseSectionDataId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onVideoRefresh: PropTypes.func,
    videoRefresh: PropTypes.bool,
    textData: PropTypes.shape({
        detail: PropTypes.string,
    }),
    textName: PropTypes.string,
    showAssignmentUpload: PropTypes.bool,
};

const styles = (theme) => ({
    uploadSection: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 20px",
        marginTop: "24px",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        backgroundColor: "#fafafa",
    },
    selectedFileSection: {
        padding: "20px 24px",
        marginTop: "24px",
        border: "1px solid #e0e0e0",
        borderRadius: "12px",
        backgroundColor: "#ffffff",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
        "@media (max-width: 600px)": {
            padding: "16px",
        },
    },
    selectedFileContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        "@media (max-width: 600px)": {
            flexDirection: "column",
            gap: "16px",
        },
    },
    selectedFileInfo: {
        display: "flex",
        alignItems: "center",
        gap: "12px",
        flex: 1,
        width: "100%",
    },
    selectedFileIcon: {
        fontSize: "40px",
        color: ColorsConstant.defaultBlue,
        flexShrink: 0,
    },
    selectedFileTextBox: {
        flex: 1,
        minWidth: 0,
    },
    selectedFileName: {
        margin: 0,
        fontSize: "15px",
        fontWeight: 600,
        color: "#1a1a1a",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    selectedFileDate: {
        margin: "4px 0 0 0",
        fontSize: "13px",
        color: "#757575",
    },
    cancelIconButton: {
        padding: "8px",
        color: "#666",
        marginLeft: "12px",
        flexShrink: 0,
    },
    selectedFileActions: {
        display: "flex",
        gap: "12px",
        "@media (max-width: 600px)": {
            width: "100%",
            "& button": {
                flex: 1,
            },
        },
    },
    previewButton: {
        textTransform: "none",
        borderColor: ColorsConstant.defaultBlue,
        color: ColorsConstant.defaultBlue,
        "&:hover": {
            borderColor: ColorsConstant.defaultBlue,
            backgroundColor: "rgba(25, 118, 210, 0.04)",
        },
    },
    uploadButton: {
        textTransform: "none",
        backgroundColor: ColorsConstant.defaultBlue,
        color: ColorsConstant.white,
        "&:hover": {
            backgroundColor: ColorsConstant.defaultBlue,
        },
    },
    uploadedSection: {
        padding: "20px 24px",
        marginTop: "24px",
        border: "1px solid #e0e0e0",
        borderRadius: "12px",
        backgroundColor: "#ffffff",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
        transition: "all 0.2s ease",
        "&:hover": {
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
        },
    },
    uploadedFileContainer: {
        display: "flex",
        alignItems: "center",
        gap: "16px",
    },
    fileIconBox: {
        width: "56px",
        height: "56px",
        borderRadius: "12px",
        backgroundColor: "#e3f2fd",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    fileIcon: {
        fontSize: "32px",
        color: ColorsConstant.defaultBlue,
    },
    fileInfoBox: {
        flex: 1,
    },
    fileNameText: {
        margin: 0,
        fontSize: "15px",
        fontWeight: 600,
        color: "#1a1a1a",
    },
    fileDateText: {
        margin: "6px 0 0 0",
        fontSize: "13px",
        color: "#757575",
    },
    statusBanner: {
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "16px",
        marginTop: "16px",
        borderRadius: "8px",
    },
    titleContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "8px",
    },
    textTitle: {
        color: ColorsConstant.defaultBlue,
        fontSize: "18px",
        fontWeight: 500,
        "@media (max-width: 959px)": {
            fontSize: '16px'
        },
    },
    fileDisplay: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
    },
    fileName: {
        fontSize: "14px",
        color: ColorsConstant.defaultBlue,
        textDecoration: "underline",
        "&:hover": {
            color: ColorsConstant.activeBlue,
        },
    },
    remarksButton: {
        textTransform: "none",
        marginLeft: "8px",
    },
    deleteButton: {
        padding: "4px",
    },
    dropZone: {
        border: "2px dashed #ccc",
        borderRadius: "8px",
        padding: "40px",
        textAlign: "center",
        cursor: "pointer",
        transition: "all 0.3s",
        "&:hover": {
            borderColor: ColorsConstant.defaultBlue,
            backgroundColor: "#f5f5f5",
        },
    },
    uploadIcon: {
        fontSize: "48px",
        color: ColorsConstant.defaultBlue,
        marginBottom: "16px",
    },
    textData: {
    color: ColorsConstant.descriptionColor,
    "& p": {
        margin: 0,

        "& img": {
            width: "40%",
            height: "auto",
            display: "block",
            margin: "16px 0px",
            cursor: "pointer",
            borderRadius: "8px",

            "@media (max-width: 767px)": {
                width: "100%",
            },
        },
    },

    "& h1, & h2, & h3, & h4, & h5, & h6": {
        margin: "12px 0px",
        fontWeight: 600,
        lineHeight: 1.4,
    },

    "& pre": {
        whiteSpace: "pre-wrap",
        overflowX: "auto",
        background: "#f5f5f5",
        padding: "12px",
        borderRadius: "6px",
    },

    /* =========================
       QUILL TABLE STYLING
    ========================= */
    "& .ql-table-wrapper": {
        width: "100%",
        overflowX: "auto",
        margin: "16px 0",
    },

    "& .ql-table": {
        width: "100% !important",
        minWidth: "600px",
        borderCollapse: "collapse",
        tableLayout: "fixed",
        background: "#fff",
        border: "1px solid #202020",
    },

    "& .ql-table-row": {
        transition: "background 0.2s ease",
    },

    "& .ql-table-cell": {
        border: "1px solid #202020 !important",
        padding: "0 !important",
        verticalAlign: "top",
        background: "#fff",
    },

    "& .ql-table-cell-inner": {
        wordBreak: "break-word",
        whiteSpace: "normal",
        fontSize: "16px",
        lineHeight: 1.6,
    },

    "& .ql-table-cell-inner p": {
        margin: 0,
    },

    "& .ql-table tr:first-child .ql-table-cell": {
        background: "#f5f7fa",
        fontWeight: 600,
    },

    "& .ql-table tr:nth-child(even) .ql-table-cell": {
        background: "#fcfcfc",
    },

    "& .ql-table tr:hover .ql-table-cell": {
        background: "#fafafa",
    },

    "@media (max-width: 959px)": {
        fontSize: "14px",

        "& .ql-table": {
            minWidth: "800px",
        },
    },
},
    modalWrapper: {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0, 0, 0, 0.95)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        zIndex: 1300,
    },
    modalContent: {
        maxWidth: "90vw",
        maxHeight: "90vh",
        overflow: "auto",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    modalImage: {
        transition: "transform 0.2s ease-in-out",
        maxWidth: "100%",
        maxHeight: "100%",
    },
    closeButton: {
        position: "absolute",
        top: 20,
        right: 20,
    },
    zoomInButton: {
        position: "absolute",
        top: 20,
        right: 70,
    },
    zoomOutButton: {
        position: "absolute",
        top: 20,
        right: 120,
    },
});

export default LearningText;
